import {
  ago,
  type Config,
  etaDisplay,
  findItem,
  firstActiveInStage,
  fmtDate,
  fmtDateTime,
  parseIsoDate,
  stageOf,
  type TicketDetail,
  type TicketPatch,
  toIsoDate,
} from '@hrms/shared'
import { useQueryClient } from '@tanstack/react-query'
import { type ReactNode, useEffect, useId, useRef, useState } from 'react'
import { LuCircleCheckBig, LuLock, LuUserPlus } from 'react-icons/lu'
import { useCurrentUser } from '../../../auth/AuthProvider.tsx'
import { Button, Checkbox, Select, TextInput, useToast } from '../../../components/ui/index.ts'
import { ApiError } from '../../../lib/api.ts'
import { cn } from '../../../lib/cn.ts'
import { hint } from '../../../lib/hint.ts'
import { ticketKeyOf, useDirectory, usePatchTicket } from './api.ts'
import { personName } from './ui.tsx'
import { WaitPrompt } from './WaitPrompt.tsx'

export const CONFLICT_TOAST = 'Someone else just updated this ticket. Showing the latest version.'
const ETA_COMMIT_DELAY_MS = 700

function Field({
  label,
  htmlFor,
  hintLine,
  children,
}: {
  label: string
  htmlFor: string
  hintLine?: ReactNode
  children: ReactNode
}) {
  return (
    <div className="mb-3.5 min-w-0">
      <label htmlFor={htmlFor} className="mb-[5px] block text-[12.5px] font-medium text-muted">
        {label}
      </label>
      {children}
      {hintLine && (
        <div className="mt-1 flex flex-wrap items-center justify-between gap-x-2 text-[12px] text-muted">
          {hintLine}
        </div>
      )}
    </div>
  )
}

const archivedSuffix = (archived: boolean) => (archived ? ' (archived)' : '')

/** Options for a picker: active items, plus the current one if it is archived (DESIGN.md §3.5). */
function pickerItems<T extends { id: string; archived: boolean }>(
  items: readonly T[],
  current: string,
) {
  return items.filter((i) => !i.archived || i.id === current)
}

/**
 * Editable side panel for the HR team (DESIGN.md §3.5 "Side panel, HR"). Every change saves at
 * once with the ticket version; a 409 refetches and says so. `onLostAccess` runs when a change
 * hides the ticket from the caller (e.g. reassigning a confidential ticket to someone else).
 */
export function HrSidePanel({
  detail,
  config,
  now,
  onLostAccess,
}: {
  detail: TicketDetail
  config: Config
  now: number
  onLostAccess: () => void
}) {
  const user = useCurrentUser()
  const toast = useToast()
  const qc = useQueryClient()
  const { ticket, people } = detail
  const mut = usePatchTicket(ticket.key)
  const directory = useDirectory()
  const tz = directory.data?.timeZone
  const ids = {
    status: useId(),
    assignee: useId(),
    priority: useId(),
    category: useId(),
    eta: useId(),
  }
  const stage = stageOf(config.statuses, ticket.statusId)
  const raiserFirst = personName(people, ticket.raisedBy).split(' ')[0] || 'the employee'
  const busy = mut.isPending

  const save = async (patch: Omit<TicketPatch, 'version'>, ok: string): Promise<boolean> => {
    try {
      const res = await mut.mutateAsync({ ...patch, version: ticket.version })
      if (res.lostAccess) {
        onLostAccess()
        return true
      }
      toast(ok)
      return true
    } catch (err) {
      if (err instanceof ApiError && err.status === 409) {
        toast(CONFLICT_TOAST)
        void qc.invalidateQueries({ queryKey: ticketKeyOf(ticket.key) })
      } else if (err instanceof ApiError && err.status === 403) {
        toast('Couldn’t save. Your access to this page may be view-only.')
      } else if (err instanceof ApiError && err.status === 404) {
        onLostAccess()
      } else {
        toast(
          err instanceof Error && err.message
            ? err.message
            : 'Couldn’t save that change. Try again.',
        )
      }
      return false
    }
  }

  // Status: moving into Waiting asks what's needed first; cancelling keeps the old status.
  const [waitFor, setWaitFor] = useState<string | null>(null)
  const onStatus = (id: string) => {
    const s = findItem(config.statuses, id)
    if (!s) return
    if (s.stage === 'waiting' && stage !== 'waiting') return setWaitFor(id)
    void save(
      { statusId: id },
      s.stage === 'done' && stage !== 'done'
        ? `${ticket.key} marked ${s.name.toLowerCase()}`
        : `Status changed to ${s.name}`,
    )
  }
  const waitStatus = findItem(config.statuses, waitFor)

  // ETA: commit picked dates after a short pause (typing a year passes through odd values).
  const iso = ticket.dueAt === null ? '' : toIsoDate(ticket.dueAt, tz)
  const [etaDraft, setEtaDraft] = useState(iso)
  const committed = useRef<string | null>(null)
  useEffect(() => {
    setEtaDraft(iso)
    committed.current = null
  }, [iso])
  const etaTimer = useRef<number | undefined>(undefined)
  const commitEta = (value: string) => {
    window.clearTimeout(etaTimer.current)
    const d = parseIsoDate(value)
    if (!d || d.year < 2000 || d.year > 2100 || value === iso || value === committed.current) return
    committed.current = value
    const label = fmtDate(Date.UTC(d.year, d.month - 1, d.day, 12), { now, timeZone: 'UTC' })
    void save({ eta: value }, `ETA set to ${label}`).then((ok) => {
      if (!ok) committed.current = null
    })
  }
  useEffect(() => () => window.clearTimeout(etaTimer.current), [])

  const eta = etaDisplay(ticket, { now, config, viewer: 'hr' })
  const resetLink = (
    <button
      type="button"
      className="font-medium text-accent hover:underline disabled:opacity-50"
      disabled={busy}
      onClick={() => void save({ eta: 'sla' }, 'ETA reset to the SLA')}
      {...hint('Go back to the created time plus the priority’s SLA')}
    >
      Reset to SLA
    </button>
  )
  let etaHint: ReactNode
  if (eta.kind === 'paused') etaHint = <span>Paused while waiting on the employee</span>
  else if (eta.kind === 'done') etaHint = <span>Stopped: the ticket is done</span>
  else if (eta.kind === 'overdue')
    etaHint = (
      <>
        <span className="font-medium text-danger">{eta.label}</span>
        {ticket.etaManual && resetLink}
      </>
    )
  else if (ticket.etaManual)
    etaHint = (
      <>
        <span>Set manually</span>
        {resetLink}
      </>
    )
  else {
    const p = findItem(config.priorities, ticket.priorityId)
    etaHint = <span>{p ? `From ${p.name} SLA (${p.slaDays}d)` : 'From the SLA'}</span>
  }

  const hrPeople = (directory.data?.people ?? []).filter((p) => p.role === 'admin')
  const assigneeMissing =
    ticket.assignee !== null && !hrPeople.some((p) => p.id === ticket.assignee)
  const requested =
    ticket.requestedPriorityId !== ticket.priorityId
      ? findItem(config.priorities, ticket.requestedPriorityId)
      : undefined
  const doneStatus = firstActiveInStage(config.statuses, 'done')

  return (
    <>
      <div className="mb-4 flex flex-wrap gap-2" data-tour="hr-quick-actions">
        {ticket.assignee !== user.id && (
          <Button
            size="sm"
            disabled={busy}
            onClick={() => void save({ assignee: user.id }, 'Assigned to you')}
            {...hint('Make yourself the assignee')}
          >
            <LuUserPlus aria-hidden className="size-3.5 text-blue-fg" />
            Assign to me
          </Button>
        )}
        {stage !== 'done' && doneStatus && (
          <Button
            size="sm"
            disabled={busy}
            onClick={() => void save({ statusId: doneStatus.id }, `${ticket.key} marked resolved`)}
            {...hint(`Move to ${doneStatus.name} and record the resolved time`)}
          >
            <LuCircleCheckBig aria-hidden className="size-3.5 text-green-fg" />
            Mark resolved
          </Button>
        )}
      </div>

      <div className="max-[980px]:grid max-[980px]:grid-cols-2 max-[980px]:gap-x-4 max-[720px]:grid-cols-1">
        <Field label="Status" htmlFor={ids.status}>
          <Select
            data-tour="hr-status"
            id={ids.status}
            value={waitFor ?? ticket.statusId}
            disabled={busy}
            onChange={(e) => onStatus(e.target.value)}
            {...hint(
              'Any active status. Moving to a Waiting status asks the employee for information first',
            )}
          >
            {pickerItems(config.statuses, ticket.statusId).map((s) => (
              <option key={s.id} value={s.id}>
                {s.name}
                {archivedSuffix(s.archived)}
              </option>
            ))}
            {!findItem(config.statuses, ticket.statusId) && (
              <option value={ticket.statusId}>Unknown status</option>
            )}
          </Select>
        </Field>

        <Field label="Assignee" htmlFor={ids.assignee}>
          <Select
            id={ids.assignee}
            value={ticket.assignee ?? ''}
            disabled={busy}
            onChange={(e) => {
              const id = e.target.value || null
              void save(
                { assignee: id },
                id
                  ? `Assigned to ${hrPeople.find((p) => p.id === id)?.name ?? 'them'}`
                  : 'Assignee removed',
              )
            }}
            {...hint('Anyone on the HR team. Confidential tickets are visible to their assignee')}
          >
            <option value="">Unassigned</option>
            {assigneeMissing && ticket.assignee && (
              <option value={ticket.assignee}>
                {personName(people, ticket.assignee)} (archived)
              </option>
            )}
            {hrPeople.map((p) => (
              <option key={p.id} value={p.id}>
                {p.name}
                {p.id === user.id ? ' (you)' : ''}
              </option>
            ))}
          </Select>
        </Field>

        <Field
          label="Priority"
          htmlFor={ids.priority}
          hintLine={requested && <span>Employee asked for {requested.name}</span>}
        >
          <Select
            id={ids.priority}
            value={ticket.priorityId}
            disabled={busy}
            onChange={(e) => {
              const p = findItem(config.priorities, e.target.value)
              void save(
                { priorityId: e.target.value },
                `Priority set to ${p?.name ?? 'the new value'}`,
              )
            }}
            {...hint(
              ticket.etaManual
                ? 'The ETA was set by hand, so it stays as it is'
                : 'Changing the priority recalculates the ETA from the created time',
            )}
          >
            {pickerItems(config.priorities, ticket.priorityId).map((p) => (
              <option key={p.id} value={p.id}>
                {p.name}
                {archivedSuffix(p.archived)}
              </option>
            ))}
          </Select>
        </Field>

        <Field label="Category" htmlFor={ids.category}>
          <Select
            id={ids.category}
            value={ticket.categoryId}
            disabled={busy}
            onChange={(e) => {
              const c = findItem(config.categories, e.target.value)
              void save({ categoryId: e.target.value }, `Moved to ${c?.name ?? 'the new category'}`)
            }}
          >
            {pickerItems(config.categories, ticket.categoryId).map((c) => (
              <option key={c.id} value={c.id}>
                {c.name}
                {archivedSuffix(c.archived)}
              </option>
            ))}
          </Select>
        </Field>

        <Field label="ETA" htmlFor={ids.eta} hintLine={etaHint}>
          <TextInput
            id={ids.eta}
            type="date"
            value={etaDraft}
            disabled={busy}
            onChange={(e) => {
              const v = e.target.value
              setEtaDraft(v)
              window.clearTimeout(etaTimer.current)
              etaTimer.current = window.setTimeout(() => commitEta(v), ETA_COMMIT_DELAY_MS)
            }}
            onBlur={(e) => commitEta(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') commitEta(e.currentTarget.value)
            }}
            className={cn(eta.kind === 'overdue' && 'border-danger')}
            {...hint('Pick a date: the ETA becomes 6 pm that day')}
          />
        </Field>
      </div>

      <div className="rounded-card border border-border bg-surface px-3 py-2.5">
        <Checkbox
          checked={ticket.confidential}
          disabled={busy}
          onChange={(e) =>
            void save(
              { confidential: e.target.checked },
              e.target.checked ? 'Marked confidential' : 'No longer confidential',
            )
          }
          className="items-start"
          label={
            <span className="block">
              <span className="flex items-center gap-1.5 font-semibold">
                <LuLock aria-hidden className="size-3.5 text-amber-fg" />
                Confidential
              </span>
              <span className="block text-[12.5px] text-muted">
                Only HR leads and the assignee can see it.
              </span>
            </span>
          }
        />
      </div>

      <dl className="mt-4 grid grid-cols-[auto_1fr] gap-x-3 gap-y-1.5 border-t border-border pt-3.5 text-[13px]">
        <dt className="text-muted">Created</dt>
        <dd className="text-right" {...hint(fmtDateTime(ticket.createdAt, { now }))}>
          {fmtDateTime(ticket.createdAt, { now })}
        </dd>
        <dt className="text-muted">Updated</dt>
        <dd className="text-right" {...hint(fmtDateTime(ticket.updatedAt, { now }))}>
          {ago(ticket.updatedAt, { now })}
        </dd>
        {ticket.resolvedAt !== null && (
          <>
            <dt className="text-muted">Resolved</dt>
            <dd className="text-right">{fmtDateTime(ticket.resolvedAt, { now })}</dd>
          </>
        )}
      </dl>

      {waitFor && waitStatus && (
        <WaitPrompt
          ticketKey={ticket.key}
          firstName={raiserFirst}
          statusName={waitStatus.name}
          onCancel={() => setWaitFor(null)}
          onSubmit={async (message) => {
            const ok = await save(
              { statusId: waitFor, waitingMessage: message },
              `Reply sent. ${ticket.key} is now waiting on ${raiserFirst}.`,
            )
            if (ok) setWaitFor(null)
            return ok
          }}
        />
      )}
    </>
  )
}
